import {Player} from 'bitmovin-player';
import {UIFactory} from 'bitmovin-player-ui';
import 'bitmovin-player-ui/dist/css/bitmovinplayer-ui.min.css';
import classnames from 'classnames';
import React, {Component} from 'react';
import Sugar from 'sugar';

const PORTAL_PLAYER_LICENSE = 'f880026c-7863-49d8-9e0d-3e4d7ec2682e';

export enum PlayerUiStyle {
  Default = 'DEFAULT',
  SmallScreen = 'SMALL_SCREEN',
  GoogleCastReceiver = 'GOOGLE_CAST_RECEIVER'
}

interface Props {
  source: any;
  config?: any;
  afterRender?: any;
  onError?: (error: string) => void;
  className?: string;
  uiStyle?: PlayerUiStyle;
  compareConfig?: (oldConfig: any, newConfig: any) => boolean;
  getCurrentImpression?: (impressionId: string) => void;
}

interface State {
  id: string;
  player?: any;
  playerUi?: any;
}

let lastPlayerId = 1;

// wrapper component for the Bitmovin Player
// reinitializes the player every time the config changes
// if the config changes frequently, use DeferredBitmovinPlayer
class BitmovinPlayer extends Component<Props, State> {
  public state = {
    id: `bitmovin-player${++lastPlayerId}`,
    player: undefined,
    playerUi: undefined
  };

  public componentDidMount() {
    this.setupPlayer(this.props);
  }

  public componentWillUnmount() {
    this.destroyPlayer();
  }

  public componentWillReceiveProps(newProps: Props) {
    if (this.shouldUpdate(this.props, newProps)) {
      this.setState({
        ...this.state,
        id: `bitmovin-player${++lastPlayerId}`
      });
    }
  }

  public shouldComponentUpdate(nextProps: Props) {
    return this.shouldUpdate(this.props, nextProps);
  }

  public shouldUpdate(thatProps, otherProps) {
    if (this.props.compareConfig) {
      return (
        !Sugar.Object.isEqual(thatProps.source, otherProps.source) ||
        !this.props.compareConfig(thatProps.config, otherProps.config)
      );
    }

    return (
      !Sugar.Object.isEqual(thatProps.source, otherProps.source) ||
      !Sugar.Object.isEqual(thatProps.config, otherProps.config)
    );
  }

  public componentDidUpdate() {
    this.destroyPlayer().then(() => {
      this.setupPlayer(this.props);
    });
  }

  public setupPlayer(props: Props) {
    const playerContainer = document.getElementById(this.state.id);
    // cannot use Object.add, because, against the docs, it modifies the original object
    let config: any = props.config != undefined ? Sugar.Object.clone(props.config, true) : {};
    config.ui = false;
    config = {
      key: PORTAL_PLAYER_LICENSE,
      playback: {
        muted: true
      },
      ...config
    };

    const uiEnabled = config.style == undefined || config.ui == undefined || config.ui === true;
    const player: any = new Player(playerContainer, config);
    player
      .load(this.props.source)
      .then(() => {
        let playerUi;
        if (uiEnabled) {
          switch (props.uiStyle) {
            case PlayerUiStyle.SmallScreen:
              playerUi = UIFactory.buildDefaultSmallScreenUI(player);
              break;
            case PlayerUiStyle.GoogleCastReceiver:
              playerUi = UIFactory.buildDefaultCastReceiverUI(player);
              break;
            default:
              playerUi = UIFactory.buildDefaultUI(player);
          }
        }
        this.setState({
          ...this.state,
          player,
          playerUi
        });
        if (props.afterRender != undefined) {
          props.afterRender(player, playerUi);
        }
        if (this.props.getCurrentImpression) {
          this.getCurrentPlayerImpressionId();
        }
      })
      .catch(error => {
        this.setState({
          ...this.state,
          player: undefined,
          playerUi: undefined
        });
        if (this.props.onError != undefined) {
          this.props.onError(error.message);
        }
      });
  }

  public destroyPlayer() {
    const player = this.state.player;
    this.setState({
      ...this.state,
      player: undefined,
      playerUi: undefined
    });

    if (player != undefined) {
      return player.destroy();
    }

    return Promise.resolve();
  }

  public async getCurrentPlayerImpressionId() {
    this.props.getCurrentImpression(await this.state.player.analytics.getCurrentImpressionId());
  }

  public render() {
    return (
      <div id={this.state.id} key={this.state.id} className={classnames('bitmovin-player', this.props.className)} />
    );
  }
}

export default BitmovinPlayer;
